/**
 * Indonesia Dot Map - frontend renderer.
 *
 * Build entry: src/js/modules/indonesia-map/frontend.js
 * Output:      assets/js/modules/indonesia-map-frontend.min.js
 *
 * Zero-dependency canvas renderer for a pre-computed Indonesia dot grid plus
 * user marker points. The dot dataset is generated offline (Web Mercator +
 * point-in-polygon scan - see scripts/generate-indonesia-dots.mjs) and ships
 * as a compact JSON beside the module; the widget's PHP render passes the
 * dataset URL + appearance config via a data attribute. All layout math
 * (fitting, marker projection) reuses the same mercator formulas at runtime,
 * so no d3, no topojson and no world-map payload is ever loaded.
 *
 * Loaded only when:
 *   1. the Indonesia Map feature is enabled (module manager),
 *   2. Elementor renders the widget on the page (get_script_depends).
 *
 * Self-contained IIFE - no imports, no build-time shared chunks.
 */
(function () {
	'use strict';

	var CONFIG_ATTR = 'data-mypro-map';

	// dataset URL -> Promise<{ cols, rows, lngMin, lngMax, yMin, yMax, bitmap }>
	var dataCache = Object.create(null);

	// Hover animation timings (ms). The label chip and the point emphasis are
	// time-based so they stay smooth regardless of frame rate.
	var LABEL_IN_MS = 420; // label chip entrance
	var LABEL_OUT_MS = 260; // label chip exit
	var HOVER_IN_MS = 380; // point glow entrance
	var HOVER_OUT_MS = 320; // point glow exit

	/** Web Mercator y (radians) for a latitude in degrees. */
	function mercY(lat) {
		return Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
	}

	/**
	 * Resolve an Elementor kit color (var(--e-global-color-*)) to a concrete
	 * value. Kit variables are scoped to `body` / `.elementor-kit-{id}`, not
	 * `:root`, so resolve against the widget subtree first (custom properties
	 * inherit), then body, then html.
	 */
	function resolveColor(value, el) {
		if (typeof value !== 'string' || value.indexOf('var(') !== 0) {
			return value;
		}
		var m = /^var\(\s*([^,\s)]+)/.exec(value);
		if (!m) {
			return value;
		}
		var candidates = [el, el && el.parentElement, document.body, document.documentElement];
		for (var i = 0; i < candidates.length; i++) {
			if (!candidates[i]) {
				continue;
			}
			var resolved = getComputedStyle(candidates[i]).getPropertyValue(m[1]).trim();
			if (resolved) {
				return resolved;
			}
		}
		return value;
	}

	var probeCanvas = null;

	/** Parse any CSS color to { r, g, b, a } (hex fast-path, 1px canvas probe fallback). */
	function parseColor(value, el) {
		var c = resolveColor(value, el);

		if (typeof c === 'string' && c.charAt(0) === '#') {
			var hex = c.slice(1);
			if (hex.length === 3) {
				hex = hex.charAt(0) + hex.charAt(0) + hex.charAt(1) + hex.charAt(1) + hex.charAt(2) + hex.charAt(2);
			}
			var n = parseInt(hex, 16);
			if (hex.length === 8) {
				return { r: (n >> 24) & 255, g: (n >> 16) & 255, b: (n >> 8) & 255, a: (n & 255) / 255 };
			}
			return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255, a: 1 };
		}

		if (!probeCanvas) {
			probeCanvas = document.createElement('canvas');
			probeCanvas.width = probeCanvas.height = 1;
		}
		var ctx = probeCanvas.getContext('2d');
		ctx.clearRect(0, 0, 1, 1);
		ctx.fillStyle = c;
		ctx.fillRect(0, 0, 1, 1);
		var d = ctx.getImageData(0, 0, 1, 1).data;
		return { r: d[0], g: d[1], b: d[2], a: d[3] / 255 };
	}

	function rgbStr(c) {
		return 'rgb(' + c.r + ',' + c.g + ',' + c.b + ')';
	}

	/** Deterministic per-cell phase in [0, 2π) for the twinkle effect. */
	function phaseFor(index) {
		var h = (index * 2654435761) >>> 0;
		h = ((h ^ (h >>> 16)) * 2246822519) >>> 0;
		return ((h % 6283) / 1000) % (Math.PI * 2);
	}

	function prefersReducedMotion() {
		return (
			typeof window.matchMedia === 'function' &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches
		);
	}

	/**
	 * Normalize a per-device setting into a { desktop, tablet, mobile } map.
	 * Accepts the plain scalar form (all devices inherit it) or the map form
	 * emitted by the widget (tablet/mobile null = inherit desktop).
	 */
	function devMap(value, def) {
		if (value && typeof value === 'object') {
			return {
				desktop: value.desktop != null ? value.desktop : def,
				tablet: value.tablet != null ? value.tablet : null,
				mobile: value.mobile != null ? value.mobile : null,
			};
		}
		return { desktop: value != null ? value : def, tablet: null, mobile: null };
	}

	/** Pick the value for the current viewport width from a device map. */
	function deviceValue(map, bp, w) {
		if (w <= bp.mobile && map.mobile != null) {
			return map.mobile;
		}
		if (w <= bp.tablet && map.tablet != null) {
			return map.tablet;
		}
		return map.desktop;
	}

	function clamp01(v) {
		return v < 0 ? 0 : v > 1 ? 1 : v;
	}

	function easeOutCubic(k) {
		return 1 - Math.pow(1 - k, 3);
	}

	/** Ease-out with a gentle overshoot past 1 (pop / settle feel). */
	function easeOutBack(k) {
		var c1 = 1.70158;
		var c3 = c1 + 1;
		return 1 + c3 * Math.pow(k - 1, 3) + c1 * Math.pow(k - 1, 2);
	}

	/** Rounded-rect path (ctx.roundRect is not universal yet). */
	function roundRectPath(ctx, x, y, w, h, r) {
		if (r > w / 2) {
			r = w / 2;
		}
		if (r > h / 2) {
			r = h / 2;
		}
		ctx.beginPath();
		ctx.moveTo(x + r, y);
		ctx.arcTo(x + w, y, x + w, y + h, r);
		ctx.arcTo(x + w, y + h, x, y + h, r);
		ctx.arcTo(x, y + h, x, y, r);
		ctx.arcTo(x, y, x + w, y, r);
		ctx.closePath();
	}

	/** Fetch + parse the dot dataset once per URL (shared across widgets). */
	function loadData(url) {
		if (!dataCache[url]) {
			dataCache[url] = fetch(url)
				.then(function (res) {
					if (!res.ok) {
						throw new Error('HTTP ' + res.status);
					}
					return res.json();
				})
				.then(function (d) {
					var bitmap = new Uint8Array(d.cols * d.rows);
					var parts = d.dots.split(',');
					for (var i = 0; i < parts.length; i += 2) {
						bitmap[Number(parts[i + 1]) * d.cols + Number(parts[i])] = 1;
					}
					return {
						cols: d.cols,
						rows: d.rows,
						lngMin: d.lngMin,
						lngMax: d.lngMax,
						yMin: d.yMin,
						yMax: d.yMax,
						bitmap: bitmap,
					};
				});
		}
		return dataCache[url];
	}

	/** Read + normalize the widget config from the container's data attribute. */
	function readConfig(container) {
		var raw = container.getAttribute(CONFIG_ATTR);
		if (!raw) {
			return null;
		}
		var cfg = JSON.parse(raw);
		cfg.density = Math.max(1, parseInt(cfg.density, 10) || 1);
		cfg.dotSize = Math.max(10, parseInt(cfg.size, 10) || 100);
		cfg.shape = cfg.shape === 'square' ? 'square' : 'circle';
		cfg.twinkle = !!cfg.twinkle && !prefersReducedMotion();
		cfg.labelFontMap = devMap(cfg.labelFont, '');
		cfg.labelSizeMap = devMap(cfg.labelSize, 13);
		cfg.labelWeightMap = devMap(cfg.labelWeight, 600);
		cfg.labelColor = typeof cfg.labelColor === 'string' ? cfg.labelColor : '';
		cfg.spacingMap = devMap(cfg.spacing, 0); // internal padding around the dot grid
		cfg.bp = { tablet: Number(cfg.bpTablet) || 1024, mobile: Number(cfg.bpMobile) || 767 };
		cfg.animStagger = Math.max(0, parseInt(cfg.delay, 10) || 500); // ms between marker entrances
		cfg.animDuration = 520; // ms per marker entrance
		cfg.pointsAnim = cfg.animPoints !== false && !prefersReducedMotion();
		cfg.pointsPulse = cfg.pulse !== false && !prefersReducedMotion();
		cfg.labelHover = cfg.labelHover !== false;
		cfg.points = (cfg.points || [])
			.filter(function (pt) {
				return pt && typeof pt === 'object';
			})
			.map(function (pt) {
				pt.lat = Number(pt.lat);
				pt.lng = Number(pt.lng);
				pt.size = Math.max(4, Number(pt.size) || 12);
				return pt;
			})
			.sort(function (a, b) {
				return a.lng - b.lng; // left-to-right appear order
			});
		return cfg;
	}

	/**
	 * Collect the fixed pixel positions of every visible dot as
	 * [x, y, phase, ...] triples. Called once per (re)layout.
	 */
	function buildDots(data, cfg, cell, ox, oy) {
		var bmp = data.bitmap;
		var stride = cfg.density;
		var cols = data.cols;
		var dots = [];
		for (var y = 0; y < data.rows; y += stride) {
			for (var x = 0; x < cols; x += stride) {
				var idx = y * cols + x;
				if (!bmp[idx]) {
					continue;
				}
				dots.push(ox + x * cell, oy + y * cell, phaseFor(idx));
			}
		}
		return dots;
	}

	/** Recompute geometry for the widget's current size; null when hidden. */
	function layout(widget) {
		var container = widget.container;
		var canvas = widget.canvas;
		var w = container.clientWidth || container.offsetWidth || 0;
		var h = container.clientHeight || container.offsetHeight || 0;
		if (!w || !h) {
			return null;
		}

		var dpr = window.devicePixelRatio || 1;
		canvas.width = Math.round(w * dpr);
		canvas.height = Math.round(h * dpr);
		canvas.style.width = w + 'px';
		canvas.style.height = h + 'px';

		var data = widget.data;

		// Configurable internal spacing: inset the dot grid from the block
		// edges (per device) so the map floats with breathing room. The cell
		// shrinks by the padded size and the centered offsets add the rest.
		var pad = deviceValue(widget.config.spacingMap, widget.config.bp, window.innerWidth || w) || 0;
		if (pad < 0) {
			pad = 0;
		} else if (pad > 400) {
			pad = 400;
		}
		var availW = w - pad * 2;
		var availH = h - pad * 2;
		if (availW < 1) {
			availW = 1;
		}
		if (availH < 1) {
			availH = 1;
		}
		var cell = Math.min(availW / data.cols, availH / data.rows);
		var ox = (w - data.cols * cell) / 2;
		var oy = (h - data.rows * cell) / 2;

		return {
			ctx: canvas.getContext('2d'),
			w: w,
			h: h,
			dpr: dpr,
			cell: cell,
			ox: ox,
			oy: oy,
			dots: buildDots(data, widget.config, cell, ox, oy),
		};
	}

	/** Paint one full frame. `t` is milliseconds since navigation start. */
	function paint(widget, frame, t) {
		var ctx = frame.ctx;
		var cfg = widget.config;
		var data = widget.data;

		ctx.setTransform(frame.dpr, 0, 0, frame.dpr, 0, 0);
		ctx.clearRect(0, 0, frame.w, frame.h);

		// Background - only painted when it has any opacity, so a fully
		// transparent pick lets the page background show through.
		var bg = parseColor(cfg.bg, widget.container);
		if (bg.a > 0) {
			ctx.globalAlpha = bg.a;
			ctx.fillStyle = rgbStr(bg);
			ctx.fillRect(0, 0, frame.w, frame.h);
			ctx.globalAlpha = 1;
		}

		// Map dots.
		var size = (cfg.dotSize / 100) * frame.cell;
		var half = size / 2;
		var dots = frame.dots;
		var isSquare = cfg.shape === 'square';
		ctx.fillStyle = rgbStr(parseColor(cfg.dot, widget.container));

		if (cfg.twinkle) {
			var s = t / 1000;
			for (var i = 0; i < dots.length; i += 3) {
				ctx.globalAlpha = 0.6 + 0.4 * (0.5 + 0.5 * Math.sin(s * 1.8 + dots[i + 2]));
				var px = dots[i];
				var py = dots[i + 1];
				if (isSquare) {
					ctx.fillRect(px - half, py - half, size, size);
				} else {
					ctx.beginPath();
					ctx.arc(px, py, half, 0, Math.PI * 2);
					ctx.fill();
				}
			}
		} else {
			for (var j = 0; j < dots.length; j += 3) {
				var qx = dots[j];
				var qy = dots[j + 1];
				if (isSquare) {
					ctx.fillRect(qx - half, qy - half, size, size);
				} else {
					ctx.beginPath();
					ctx.arc(qx, qy, half, 0, Math.PI * 2);
					ctx.fill();
				}
			}
		}
		ctx.globalAlpha = 1;

		paintMarkers(ctx, widget, frame, t);
	}

	/**
	 * Draw user marker points in two passes:
	 *
	 * Pass 1 - every point: an outer circle that continuously expands and
	 * fades (ping), or a static halo when animation is off, plus the solid
	 * core dot. The hovered point gets a soft glow + a brighter core (driven
	 * by widget.hoverBoost).
	 *
	 * Pass 2 - labels, drawn AFTER all points so they always sit on top of
	 * the map dots and neighbouring markers (never underneath them). The
	 * hovered label animates in as a pill chip that pops out of the point
	 * with a slide + scale + fade (ease-out-back). When "Show Label on
	 * Hover" is off every label is drawn statically.
	 */
	function paintMarkers(ctx, widget, frame, t) {
		var cfg = widget.config;
		var data = widget.data;
		var points = cfg.points || [];

		if (!points.length) {
			return;
		}

		// One font per paint: responsive family/size/weight maps are resolved
		// against the current viewport width (Elementor breakpoints).
		var innerW = window.innerWidth || frame.w;
		var bp = cfg.bp;
		var lf = deviceValue(cfg.labelFontMap, bp, innerW);
		var ls = deviceValue(cfg.labelSizeMap, bp, innerW);
		var lw = deviceValue(cfg.labelWeightMap, bp, innerW);
		var fontStack = lf ? lf + ', sans-serif' : 'system-ui, -apple-system, sans-serif';
		ctx.font = lw + ' ' + ls + 'px ' + fontStack;
		ctx.textBaseline = 'middle';

		// Optional global label color (empty = inherit each point's color).
		var labelColor = null;
		if (cfg.labelColor) {
			var lc = parseColor(cfg.labelColor, widget.container);
			labelColor = lc ? rgbStr(lc) : null;
		}

		var t0 = widget.animStart; // base timestamp of the running animation loop
		var appearOn = cfg.pointsAnim;
		var pulseOn = cfg.pointsPulse;
		var pingPeriod = 2.2; // seconds per full ring expansion
		var labels = []; // deferred label paints (drawn on top, second pass)

		for (var i = 0; i < points.length; i++) {
			var pt = points[i];
			var nx = ((pt.lng - data.lngMin) / (data.lngMax - data.lngMin)) * data.cols;
			var ny = ((data.yMax - mercY(pt.lat)) / (data.yMax - data.yMin)) * data.rows;
			var px = frame.ox + nx * frame.cell;
			var py = frame.oy + ny * frame.cell;
			var col = parseColor(pt.color, widget.container);
			var r = pt.size / 2;

			// Appear progress: marker i enters at i * animStagger ms.
			var p = 1;
			if (appearOn) {
				var elapsed = (t - t0 - i * cfg.animStagger) / cfg.animDuration;
				p = elapsed < 0 ? 0 : elapsed > 1 ? 1 : elapsed;
			}
			if (p < 0.01) {
				continue; // not visible yet
			}
			var e = 1 - Math.pow(1 - p, 3); // ease-out cubic fade
			var pop = p >= 1 ? 1 : 1 + 2.70158 * Math.pow(p - 1, 3) + 1.70158 * Math.pow(p - 1, 2); // ease-out-back scale
			var cr = r * pop;
			ctx.fillStyle = rgbStr(col);

			// Outer circle: expanding ping (offset per point so they don't pulse in unison).
			if (pulseOn) {
				var phase = ((t / 1000) + i * 0.53) % pingPeriod;
				var q = phase / pingPeriod; // 0..1
				ctx.globalAlpha = 0.32 * (1 - q) * e;
				ctx.beginPath();
				ctx.arc(px, py, cr * (2.1 + 2.2 * q), 0, Math.PI * 2);
				ctx.fill();
			} else {
				ctx.globalAlpha = 0.25 * e;
				ctx.beginPath();
				ctx.arc(px, py, cr * 2.4, 0, Math.PI * 2);
				ctx.fill();
			}

			// Hover emphasis: soft colored glow + bright rim + bigger core.
			var hov = widget.hoverIndex === i ? widget.hoverBoost : 0;
			if (hov > 0.01) {
				var boost = 1 + 0.45 * easeOutBack(hov);
				var gcr = cr * boost;
				ctx.globalAlpha = 0.3 * hov * e;
				ctx.beginPath();
				ctx.arc(px, py, gcr * 2.2, 0, Math.PI * 2);
				ctx.fill();
				ctx.globalAlpha = 0.85 * hov * e;
				ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
				ctx.lineWidth = 1.5;
				ctx.beginPath();
				ctx.arc(px, py, gcr + 2.5, 0, Math.PI * 2);
				ctx.stroke();
				cr = gcr;
			}

			// Core dot.
			ctx.globalAlpha = e;
			ctx.beginPath();
			ctx.arc(px, py, cr, 0, Math.PI * 2);
			ctx.fill();

			// Label: hidden until hover (animated chip), or always visible
			// when the hover option is off. labelPoint persists through the
			// fade-out so the label keeps drawing while its alpha animates.
			// Labels are deferred to a second pass so they render on top of
			// every point, never underneath a later one.
			if (pt.label && (!cfg.labelHover || widget.labelPoint === i)) {
				labels.push({
					px: px,
					py: py,
					cr: cr,
					col: col,
					text: pt.label,
					k: cfg.labelHover ? widget.labelAlpha : 1,
				});
			}
		}
		ctx.globalAlpha = 1;

		// Pass 2: labels on top of everything.
		for (var li = 0; li < labels.length; li++) {
			var L = labels[li];
			if (L.k > 0.01) {
				drawLabelChip(ctx, widget, frame, L, ls, labelColor);
			}
		}
		ctx.globalAlpha = 1;
	}

	/**
	 * Draw one label as a pill chip that emerges from its point: the chip
	 * center slides from the point out to its resting position while scaling
	 * from 60% up with an ease-out-back overshoot, over a soft colored glow,
	 * a subtle connector line and a dark glass body with a colored accent
	 * dot. `k` (0..1) drives the whole entrance/exit (labelAlpha).
	 */
	function drawLabelChip(ctx, widget, frame, L, fontSize, labelColor) {
		var text = L.text;
		var k = clamp01(L.k);
		if (k < 0.01) {
			return;
		}

		var padX = 11;
		var dotR = 3.5;
		var gap = 8;
		var textW = ctx.measureText(text).width;
		var w = textW + padX * 2 + dotR * 2 + gap;
		var h = Math.max(26, fontSize + 13);
		var radius = h / 2; // pill

		// Resting position: right of the point; flip to the left when it
		// would overflow the canvas, and clamp vertically inside it.
		var x = L.px + L.cr + 10;
		var flip = x + w > frame.w - 6;
		if (flip) {
			x = L.px - L.cr - 10 - w;
		}
		var y = L.py - h / 2;
		if (y < 6) {
			y = 6;
		} else if (y + h > frame.h - 6) {
			y = frame.h - h - 6;
		}
		var cx = x + w / 2;
		var cy = y + h / 2;

		// Emerge: interpolate the chip center from the point to its resting
		// spot with an ease-out, and scale from 60% with ease-out-back.
		var ease = easeOutCubic(k);
		var scale = 0.6 + 0.4 * easeOutBack(k);
		var ix = L.px + (cx - L.px) * ease;
		var iy = L.py + (cy - L.py) * ease;

		// Connector line: point -> nearest chip edge (tracks the animation).
		var ex = Math.max(ix, Math.min(L.px, ix + w));
		var ey = Math.max(iy, Math.min(L.py, iy + h));
		ctx.save();
		ctx.globalAlpha = 0.35 * k;
		ctx.strokeStyle = rgbStr(L.col);
		ctx.lineWidth = 1;
		ctx.lineCap = 'round';
		ctx.beginPath();
		ctx.moveTo(L.px, L.py);
		ctx.lineTo(ex, ey);
		ctx.stroke();
		ctx.restore();

		ctx.save();
		ctx.translate(ix, iy);
		ctx.scale(scale, scale);
		ctx.translate(-cx, -cy);

		// Soft colored glow halo.
		ctx.globalAlpha = 0.16 * k;
		ctx.fillStyle = rgbStr(L.col);
		roundRectPath(ctx, x - 4, y - 4, w + 8, h + 8, radius + 3);
		ctx.fill();

		// Dark glass body.
		ctx.globalAlpha = 0.94 * k;
		ctx.fillStyle = 'rgba(3, 7, 18, 0.92)';
		roundRectPath(ctx, x, y, w, h, radius);
		ctx.fill();

		// Colored border.
		ctx.globalAlpha = 0.6 * k;
		ctx.strokeStyle = rgbStr(L.col);
		ctx.lineWidth = 1;
		roundRectPath(ctx, x, y, w, h, radius);
		ctx.stroke();

		// Colored accent dot + label text.
		ctx.globalAlpha = k;
		ctx.fillStyle = rgbStr(L.col);
		ctx.beginPath();
		ctx.arc(x + padX + dotR, cy, dotR, 0, Math.PI * 2);
		ctx.fill();
		ctx.fillStyle = labelColor || rgbStr(L.col);
		ctx.textAlign = 'left';
		ctx.textBaseline = 'middle';
		ctx.fillText(text, x + padX + dotR * 2 + gap, cy);

		ctx.restore();
	}

	/** Project a point to CSS pixels on the current frame. */
	function pointPos(widget, pt) {
		var data = widget.data;
		var frame = widget.frame;
		if (!data || !frame) {
			return { x: -1e4, y: -1e4 };
		}
		var nx = ((pt.lng - data.lngMin) / (data.lngMax - data.lngMin)) * data.cols;
		var ny = ((data.yMax - mercY(pt.lat)) / (data.yMax - data.yMin)) * data.rows;
		return { x: frame.ox + nx * frame.cell, y: frame.oy + ny * frame.cell };
	}

	/** Hit-test the pointer against the marker points. */
	function hoverPoint(widget, mx, my) {
		var pts = widget.config.points || [];
		var idx = -1;
		var best = 26;
		for (var i = 0; i < pts.length; i++) {
			var pos = pointPos(widget, pts[i]);
			var dx = mx - pos.x;
			var dy = my - pos.y;
			var d = Math.sqrt(dx * dx + dy * dy);
			var radius = Math.max(18, pts[i].size);
			if (d < best && d < radius + 12) {
				best = d;
				idx = i;
			}
		}
		return idx;
	}

	function setHover(widget, idx) {
		if (widget.hoverIndex === idx) {
			return;
		}
		widget.hoverIndex = idx;
		widget.canvas.style.cursor = idx >= 0 ? 'pointer' : '';
		if (prefersReducedMotion()) {
			widget.labelPoint = idx;
			widget.labelAlpha = idx >= 0 ? 1 : 0;
			widget.hoverBoost = idx >= 0 ? 1 : 0;
			startAnimation(widget); // single static repaint, no fade
			return;
		}
		// The label only needs a target point while hover-reveal is on;
		// labelPoint persists through the fade-out so the chip keeps drawing
		// as it animates away.
		if (widget.config.labelHover && idx >= 0) {
			widget.labelPoint = idx;
		}
		// The point emphasis (glow + bigger core) animates even when labels
		// are always visible, so always run the loop on hover changes.
		ensureLoop(widget);
	}

	function onPointerMove(widget, e) {
		var rect = widget.canvas.getBoundingClientRect();
		setHover(widget, hoverPoint(widget, e.clientX - rect.left, e.clientY - rect.top));
	}

	function onPointerLeave(widget) {
		setHover(widget, -1);
	}

	/** One rAF tick: advance appear + hover animations, paint; true = keep looping. */
	function loopTick(widget, frame, t) {
		if (!widget.container.isConnected) {
			return false;
		}
		if (widget.animStart === 0 && widget.config.pointsAnim) {
			widget.animStart = t;
		}

		var cfg = widget.config;

		// Label reveal: time-based 0..1 progress toward the target (1 =
		// hovered). Seeding the start time a hair in the past makes the first
		// hover frame already show a hint of the chip instead of a blank one.
		var labelTarget = cfg.labelHover && widget.hoverIndex >= 0 ? 1 : 0;
		if (widget.labelTarget !== labelTarget) {
			widget.labelTarget = labelTarget;
			widget.labelAnimT0 = t - 24;
		}
		if (cfg.labelHover) {
			var lk = (t - widget.labelAnimT0) / (labelTarget ? LABEL_IN_MS : LABEL_OUT_MS);
			lk = clamp01(lk);
			widget.labelAlpha = labelTarget ? easeOutCubic(lk) : 1 - easeOutCubic(lk);
			if (!labelTarget && widget.labelAlpha === 0) {
				widget.labelPoint = -1;
			}
		}

		// Point hover emphasis (glow + bigger core) - runs independently of
		// the labels so the point reacts even with labels always visible.
		var hbTarget = widget.hoverIndex >= 0 ? 1 : 0;
		if (widget.hoverBoostTarget !== hbTarget) {
			widget.hoverBoostTarget = hbTarget;
			widget.hoverBoostT0 = t - 24;
		}
		var hbk = (t - widget.hoverBoostT0) / (hbTarget ? HOVER_IN_MS : HOVER_OUT_MS);
		hbk = clamp01(hbk);
		widget.hoverBoost = hbTarget ? easeOutBack(hbk) : 1 - easeOutCubic(hbk);

		paint(widget, frame, t);

		var appearRunning = cfg.pointsAnim && t - widget.animStart < widget.animTotal;
		var labelSettled = !cfg.labelHover || widget.labelAlpha === labelTarget;
		var hoverSettled = widget.hoverBoost === hbTarget;
		return (
			cfg.twinkle ||
			cfg.pointsPulse ||
			appearRunning ||
			!labelSettled ||
			!hoverSettled
		);
	}

	/** (Re)layout + paint; run the rAF loop while any animation is active. */
	function startAnimation(widget) {
		stopAnimation(widget);

		var frame = layout(widget);
		if (!frame) {
			return;
		}
		widget.frame = frame;
		widget.animStart = 0; // sentinel: base timestamp set by the first loop frame

		var pts = widget.config.points || [];
		widget.animTotal = pts.length ? widget.config.animStagger * (pts.length - 1) + widget.config.animDuration : 0;

		var labelNeeds = widget.config.labelHover && widget.labelAlpha !== (widget.hoverIndex >= 0 ? 1 : 0);
		var hoverNeeds = widget.hoverBoost !== (widget.hoverIndex >= 0 ? 1 : 0);
		var needsLoop = widget.config.twinkle || widget.config.pointsAnim || widget.config.pointsPulse || labelNeeds || hoverNeeds;
		if (!needsLoop) {
			paint(widget, frame, 0);
			return;
		}

		// Frame 0: dots + background paint immediately; markers animate in.
		paint(widget, frame, 0);
		ensureLoop(widget);
	}

	/** Resume the rAF loop without restarting the appear sequence. */
	function ensureLoop(widget) {
		if (widget.raf || !widget.frame) {
			return;
		}
		var frame = widget.frame;
		widget.raf = requestAnimationFrame(function loop(t) {
			if (loopTick(widget, frame, t)) {
				widget.raf = requestAnimationFrame(loop);
			} else {
				widget.raf = 0;
			}
		});
	}

	function stopAnimation(widget) {
		if (widget.raf) {
			cancelAnimationFrame(widget.raf);
			widget.raf = 0;
		}
		widget.animStart = 0;
	}

	function showError(container) {
		var el = document.createElement('div');
		el.className = 'mypro-indonesia-map__error';
		el.textContent = 'Map data failed to load.';
		container.appendChild(el);
	}

	/** Initialize one widget container (deduped; re-inits in place on config change). */
	function initWidget(container) {
		if (!container) {
			return;
		}

		var raw = container.getAttribute(CONFIG_ATTR);
		if (!raw) {
			return;
		}
		var canvas = container.querySelector('.mypro-indonesia-map__canvas');
		if (!canvas) {
			return;
		}

		// Re-render path: the editor updates the data attribute in place on the
		// same node (no replacement). Diff the raw config and re-init when it
		// changed so control edits (colors, size, points, ...) repaint live.
		if (container.__myproMapWidget) {
			var existing = container.__myproMapWidget;
			if (existing.configRaw === raw) {
				return;
			}
			existing.config = readConfig(container);
			existing.configRaw = raw;
			if (existing.dataUrl !== existing.config.data) {
				existing.dataUrl = existing.config.data;
				existing.data = null;
				loadData(existing.config.data)
					.then(function (data) {
						existing.data = data;
						startAnimation(existing);
					})
					.catch(function () {
						showError(container);
					});
			} else if (existing.data) {
				startAnimation(existing);
			}
			return;
		}

		var cfg = readConfig(container);
		if (!cfg) {
			return;
		}

		var widget = {
			container: container,
			canvas: canvas,
			config: cfg,
			configRaw: raw,
			dataUrl: cfg.data,
			data: null,
			frame: null,
			raf: 0,
			ro: null,
			hoverIndex: -1,
			hoverBoost: 0,
			hoverBoostTarget: 0,
			hoverBoostT0: 0,
			labelAlpha: 0,
			labelTarget: 0,
			labelAnimT0: 0,
			labelPoint: -1,
		};
		container.__myproMapWidget = widget;
		widgets.push(widget);

		// Hover reveals the point label (fades in via the rAF loop).
		canvas.addEventListener('pointermove', function (e) {
			onPointerMove(widget, e);
		});
		canvas.addEventListener('pointerleave', function () {
			onPointerLeave(widget);
		});

		loadData(cfg.data)
			.then(function (data) {
				widget.data = data;
				startAnimation(widget);
			})
			.catch(function () {
				showError(container);
			});

		// Redraw when the container resizes (sidebar collapse, viewport change,
		// tab widgets becoming visible, ...).
		if (typeof ResizeObserver !== 'undefined') {
			widget.ro = new ResizeObserver(function () {
				if (widget.data) {
					startAnimation(widget);
				}
			});
			widget.ro.observe(container);
		}
	}

	// --- initialization ---------------------------------------------------

	var widgets = []; // live widget states, for cleanup of detached nodes

	/**
	 * Register the Elementor `element_ready` hook. `elementorFrontend.hooks`
	 * only exists after `elementorFrontend.init()` runs - on the public page at
	 * DOM ready, and in the editor preview after the parent editor boots and
	 * calls `elementorFrontend.init()` on the iframe's instance. `init()`
	 * dispatches the native "elementor/frontend/init" event on `window` at
	 * that exact moment, so we register from there rather than at script eval
	 * (where hooks does not exist yet inside the editor).
	 *
	 * `runReadyTrigger` fires `frontend/element_ready/<data-widget_type>` and
	 * the rendered wrapper always carries the skin suffix (e.g.
	 * `mypro_indonesia_map.default`), so the suffixed name is the one that
	 * actually fires; the bare name is registered too for older versions.
	 */
	function registerElementReadyHook() {
		if (
			!window.elementorFrontend ||
			!window.elementorFrontend.hooks ||
			window.elementorFrontend.hooks.__myproIndonesiaMapReady
		) {
			return;
		}
		window.elementorFrontend.hooks.__myproIndonesiaMapReady = true;

		var onReady = function ($scope) {
			initWidget($scope && $scope[0] ? $scope[0] : $scope);
		};
		window.elementorFrontend.hooks.addAction('frontend/element_ready/mypro_indonesia_map.default', onReady);
		window.elementorFrontend.hooks.addAction('frontend/element_ready/mypro_indonesia_map', onReady);
	}

	/** Drop state for widgets whose containers left the DOM (editor re-renders). */
	function cleanupDetached() {
		for (var i = widgets.length - 1; i >= 0; i--) {
			var w = widgets[i];
			if (!w.container.isConnected) {
				stopAnimation(w);
				if (w.ro) {
					w.ro.disconnect();
					w.ro = null;
				}
				widgets.splice(i, 1);
			}
		}
	}

	/** Sweep the document for (new) widget containers. */
	function init() {
		cleanupDetached();
		var nodes = document.querySelectorAll('[' + CONFIG_ATTR + ']');
		for (var i = 0; i < nodes.length; i++) {
			initWidget(nodes[i]);
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

	if (window.elementorFrontend && window.elementorFrontend.hooks) {
		registerElementReadyHook();
	}
	window.addEventListener('elementor/frontend/init', registerElementReadyHook);

	// Version-proof fallback: whenever a widget node appears after this script
	// ran (editor drag-in, control re-render, lazy tabs/accordions, late
	// renderers), a rAF-throttled sweep initializes it - no Elementor internals.
	var sweepQueued = false;
	function queueSweep() {
		if (sweepQueued) {
			return;
		}
		sweepQueued = true;
		requestAnimationFrame(function () {
			sweepQueued = false;
			init();
		});
	}
	if (typeof MutationObserver !== 'undefined') {
		new MutationObserver(function () {
			queueSweep();
		}).observe(document.documentElement, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: [CONFIG_ATTR],
		});
	}
})();
